import { useFeatureFlags } from '@brightdrop/feature-flags-client';

/**
 * LaunchDarkly flag key that gates the AI Sidekick panel and entry
 * button across all consumer surfaces. When the flag is off, consumers
 * that go through `useIsSidekickEnabled()` render exactly as before:
 * no panel mounts, no entry button appears, no layout wrapper is
 * inserted.
 */
export const AI_SIDEKICK_FLAG = 'SDPCDXP-1496-AI-Sidekick';

/**
 * Single source of truth for "should the AI Sidekick UI be visible?".
 *
 * Resolves `AI_SIDEKICK_FLAG` against the user context the consumer's
 * `FeatureFlagsProvider` is initialized with. Every Sidekick consumer
 * (entry button, panel host, layout shell) goes through this hook so
 * there is exactly one place to disable Sidekick at rollout time.
 *
 * Strictly returns `true` only when `getFlag` resolves to the boolean
 * literal `true`. Per `FeatureFlagValueType` (defined in
 * `@brightdrop/feature-flags-client`), `getFlag` may resolve to a
 * `string`, `boolean`, `number`, or recursive flag-payload object — so
 * loose coercion would silently enable the feature if an LD admin ever
 * flipped this key from a binary kill-switch to a multivariate config
 * (e.g., a `'control'` variation is truthy). Strict equality enforces
 * the binary contract at the code boundary.
 */
export function useIsSidekickEnabled(): boolean {
  const { getFlag } = useFeatureFlags();
  return getFlag(AI_SIDEKICK_FLAG) === true;
}
